import * as React from 'react';
import { SpinnerGap } from '@phosphor-icons/react';

import { useFontOptions } from './use-font-options';
import { FontPickerItem } from './font-picker-item';

import { Combobox } from '@/components/combobox/combobox';

export interface FontPickerProps {
  /** Currently selected font family */
  value?: string;
  /** Callback when font selection changes */
  onChange?: (fontFamily: string) => void;
  /** Google Fonts API key (required) */
  apiKey: string;
  /** Placeholder text when no font is selected */
  placeholder?: string;
  /** Text shown in search input */
  searchPlaceholder?: string;
  /** Text shown when no fonts match the search */
  noResultsText?: string;
  /** Text shown while fonts are loading */
  loadingText?: string;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** Additional CSS classes for the trigger button */
  className?: string;
  /** Width of the dropdown (defaults to trigger width) */
  dropdownWidth?: number;
  /** Maximum number of fonts to display (for performance) */
  maxFonts?: number;
  /** Callback when an error occurs fetching fonts */
  onError?: (error: Error) => void;
}

/**
 * FontPicker - A searchable dropdown for selecting Google Fonts
 *
 * @example
 * ```tsx
 * const [font, setFont] = useState('Inter');
 *
 * <FontPicker
 *   apiKey="YOUR_API_KEY"
 *   value={font}
 *   onChange={setFont}
 *   placeholder="Select a font"
 * />
 * ```
 */
export function FontPicker({
  value,
  onChange,
  apiKey,
  placeholder = 'Select a font...',
  searchPlaceholder = 'Search fonts...',
  noResultsText = 'No fonts found.',
  loadingText = 'Loading fonts...',
  disabled = false,
  className,
  dropdownWidth,
  maxFonts = 500,
  onError,
}: FontPickerProps) {
  const { fonts, isLoading, loadFont } = useFontOptions({ apiKey, maxFonts, onError });

  // Load font stylesheet when selected
  React.useEffect(() => {
    if (value) {
      loadFont(value);
    }
  }, [value, loadFont]);

  // fonts already have value/label which Combobox requires
  const options = fonts;

  return (
    <Combobox
      options={options}
      value={value}
      onChange={onChange}
      defaultValueLabel={placeholder}
      placeholder={searchPlaceholder}
      noResultsLabel={noResultsText}
      disabled={disabled || isLoading}
      className={className}
      dropdownWidth={dropdownWidth}
      renderTrigger={({ selectedItem }) => {
        if (isLoading) {
          return (
            <span className="flex items-center gap-2 text-muted-foreground">
              <SpinnerGap className="h-4 w-4 animate-spin" />
              {loadingText}
            </span>
          );
        }

        if (selectedItem) {
          return <span style={{ fontFamily: selectedItem.value }}>{selectedItem.label}</span>;
        }

        return <span className="text-muted-foreground">{placeholder}</span>;
      }}
      renderItem={({ item, isSelected, onSelect }) => (
        <FontPickerItem font={item} isSelected={isSelected} onSelect={onSelect} />
      )}
    />
  );
}

export default FontPicker;
